/**
 * Sistema de logging centralizado
 */

export enum LogLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG'
}

/**
 * Configuração global do logger
 */
const LOG_CONFIG = {
  level: process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.ERROR,
  maxStoredLogs: 50,
  consoleOutput: true,
  storeLocally: true
};

/**
 * Interface para mensagens de log
 */
interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
}

/**
 * Armazenamento de logs recentes
 */
const logs: LogEntry[] = [];

/**
 * Log de erro
 */
export const logError = (category: string, message: string, data?: any): void => {
  logMessage(LogLevel.ERROR, category, message, data);
};

/**
 * Log de alerta
 */
export const logWarn = (category: string, message: string, data?: any): void => {
  logMessage(LogLevel.WARN, category, message, data);
};

/**
 * Log informativo
 */
export const logInfo = (category: string, message: string, data?: any): void => {
  logMessage(LogLevel.INFO, category, message, data);
};

/**
 * Log de depuração (apenas em desenvolvimento)
 */
export const logDebug = (category: string, message: string, data?: any): void => {
  logMessage(LogLevel.DEBUG, category, message, data);
};

/**
 * Função base para geração de logs
 */
function logMessage(level: LogLevel, category: string, message: string, data?: any): void {
  // Verificar se devemos registrar este nível de log
  if (shouldLog(level)) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data
    };
    
    // Salvar no array de logs recentes
    logs.push(entry);
    
    // Remover logs mais antigos se exceder o limite
    if (logs.length > LOG_CONFIG.maxStoredLogs) {
      logs.shift();
    }
    
    // Exibir no console
    if (LOG_CONFIG.consoleOutput) {
      outputToConsole(entry);
    }
    
    // Armazenar localmente
    if (LOG_CONFIG.storeLocally) {
      storeLogLocally(entry);
    }
  }
}

/**
 * Determina se o nível de log deve ser registrado
 */
function shouldLog(level: LogLevel): boolean {
  const levels = [LogLevel.ERROR, LogLevel.WARN, LogLevel.INFO, LogLevel.DEBUG];
  const configLevelIndex = levels.indexOf(LOG_CONFIG.level);
  const currentLevelIndex = levels.indexOf(level);
  
  return currentLevelIndex <= configLevelIndex;
}

/**
 * Formata e exibe o log no console
 */
function outputToConsole(entry: LogEntry): void {
  const { level, category, message, data } = entry;
  
  // Definir estilo com base no nível
  let style = '';
  
  switch (level) {
    case LogLevel.ERROR:
      style = 'color: #FD5D5D; font-weight: bold';
      break;
    case LogLevel.WARN:
      style = 'color: #FFC436; font-weight: bold';
      break;
    case LogLevel.INFO:
      style = 'color: #2D9596; font-weight: bold';
      break;
    default:
      style = 'color: #827397; font-weight: bold';
  }
  
  // Exibir log formatado
  console.groupCollapsed(`%c[${level}] [${category}] ${message}`, style);
  if (data !== undefined) {
    console.log('Detalhes:', data);
  }
  console.groupEnd();
}

/**
 * Armazena logs no localStorage
 */
function storeLogLocally(entry: LogEntry): void {
  // Verificar se estamos em ambiente com localStorage disponível
  if (typeof window === 'undefined' || !window.localStorage) {
    console.log('LocalStorage não disponível para armazenamento de logs');
    return;
  }
  
  try {
    // Obter logs armazenados
    const storedLogs = JSON.parse(localStorage.getItem('app_logs') || '[]');
    
    // Adicionar novo log
    storedLogs.push(entry);
    
    // Limitar quantidade
    const logs = storedLogs.slice(-LOG_CONFIG.maxStoredLogs);
    
    // Salvar
    localStorage.setItem('app_logs', JSON.stringify(logs));
  } catch (e) {
    // Ignorar erros de storage
    console.error('Erro ao salvar log no localStorage:', e);
  }
}

/**
 * Obtém logs armazenados
 */
export const getStoredLogs = (): LogEntry[] => {
  try {
    return JSON.parse(localStorage.getItem('app_logs') || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Limpa logs armazenados
 */
export const clearLogs = (): void => {
  logs.length = 0;
  try {
    localStorage.removeItem('app_logs');
  } catch (e) {
    // Ignorar erros
  }
};
